// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getBillTosDataView,
    getCurrentBillToState,
} from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTos from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTos";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import loadCurrentShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTo";
import loadCurrentShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTos";
import {
    getCurrentShipTosDataView,
    getCurrentShipToState,
} from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Truck from "@insite/mobius/Icons/Truck";
import Upload from "@insite/mobius/Icons/Upload";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ChevronDown, { ChevronDownOffset } from "@store/Library/Components/Toolkit/Icons/ChevronDown";
import setChangeCustomerModalIsOpen from "@store/Framework/Store/Greif/Components/ChangeCustomerModal/Handlers/ChangeCustomerIsOpen";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { siteWidthStyles } from "@toolkit/Themes/common/mixins";
import { iconScalableStyles } from "@toolkit/Themes/common/mixins";
import isArray from "lodash/isArray";
import React, { FC, useContext, useEffect, useMemo } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const billTo = getCurrentBillToState(state).value;

    return {
        billTo,
        shipTo: getCurrentShipToState(state).value,
        billTosDataView: getBillTosDataView(state, {}),
        shipTosDataView: getCurrentShipTosDataView(state),
        isAuthenticated: state.context.session.isAuthenticated,
        orderUploadLink: getPageLinkByPageType(state, "OrderUploadPage"),
    };
};

const mapDispatchToProps = {
    loadBillTos,
    loadCurrentShipTos,
    loadCurrentBillTo,
    loadCurrentShipTo,
    setChangeCustomerModalIsOpen,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CurrentAccountNavigationStyles {
    wrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    section?: InjectableCss;
    textlabel?: TypographyPresentationProps;
    textAddress?: TypographyPresentationProps;
    pipeStyles?: TypographyPresentationProps;
    iconTruck?: IconProps;
    orderUpload?: LinkPresentationProps;
    iconDropdown?: IconProps;
}

export const styles: CurrentAccountNavigationStyles = {
    wrapper: {
        css: css`
            background-color: ${getColor("common.accent")};
            height: ${pxToRem(42)};
            font-size: ${x => x.theme.design.typography.size.smaller};
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    innerWrapper: {
        css: css`
            ${siteWidthStyles()}
            display: flex;
            justify-content: space-between;
            height: inherit;
        `,
    },
    section: {
        css: css`
            display: flex;
            align-items: center;
            gap: ${pxToRem(10)};
            max-width: 60vw;

            span {
                white-space: nowrap;
            }
        `,
    },
    textAddress: {
        ellipsis: true,
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.base};
        `,
    },
    pipeStyles: {
        color: "text.disabled",
    },
    iconTruck: {
        src: Truck,
        css: css`
            color: ${getColor("primary")};
        `,
    },
    orderUpload: {
        icon: {
            iconProps: {
                src: Upload,
                css: css`
                    color: ${getColor("primary")};
                    margin-right: ${pxToRem(5)};
                `,
            },
            position: "left",
        },
        css: css`
            white-space: nowrap;
        `,
    },
    iconDropdown: {
        src: ChevronDown,
        color: "inherit",
        css: css`
            ${iconScalableStyles("1em", ChevronDownOffset)}
            margin-left: 0.5em;
            position: static;
        `,
    },
};

const CurrentAccount: FC<Props> = ({
    billTo,
    shipTo,
    loadBillTos,
    loadCurrentShipTos,
    billTosDataView,
    shipTosDataView,
    loadCurrentBillTo,
    loadCurrentShipTo,
    isAuthenticated,
    orderUploadLink,
    setChangeCustomerModalIsOpen,
}) => {
    const { isEditing, isInShell } = useContext(ShellContext);

    const showChangeCustomerLink =
        (isArray(billTosDataView.value) && billTosDataView.value.length >= 2) ||
        (isArray(shipTosDataView.value) && shipTosDataView.value.length >= 4);

    const wrapperCss = useMemo(() => {
        return css`
            ${styles.wrapper?.css}
            ${isEditing ? "height: auto;" : null}
            ${!isEditing && isInShell ? "[data-widget] { height: 100%; }" : null}
        `;
    }, [styles, isEditing, isInShell]);

    useEffect(() => {
        if (isAuthenticated) {
            if (!billTo) {
                loadCurrentBillTo();
            }
            if (!shipTo) {
                loadCurrentShipTo();
            }
            if (!billTosDataView.isLoading && !billTosDataView.value) {
                loadBillTos();
            }
            if (!shipTosDataView.isLoading && !shipTosDataView.value) {
                loadCurrentShipTos();
            }
        }
    }, []);

    return (
        <StyledWrapper {...styles.wrapper} css={wrapperCss}>
            <StyledWrapper className="wrapper" {...styles.innerWrapper}>
                <>
                    {isAuthenticated && billTo && shipTo ? (
                        <StyledWrapper className="zone-left" {...styles.section}>
                            <Icon {...styles.iconTruck} />
                            <Typography {...styles.textlabel}>{`${translate("Sold to")}: `}</Typography>
                            <Typography
                                {...styles.textAddress}
                            >{`${billTo?.companyName} ${billTo?.fullAddress}`}</Typography>
                            <Typography {...styles.pipeStyles}>|</Typography>
                            <Typography {...styles.textlabel}>{`${translate("Ship to")}: `}</Typography>
                            <Typography
                                {...styles.textAddress}
                            >{`${shipTo?.companyName} ${shipTo?.fullAddress} `}</Typography>
                            {showChangeCustomerLink && (
                                <Link
                                    icon={{
                                        position: "right",
                                        iconProps: { ...styles.iconDropdown },
                                    }}
                                    onClick={() => setChangeCustomerModalIsOpen({ isOpen: true })}
                                >
                                    {translate("Change Account")}
                                </Link>
                            )}
                        </StyledWrapper>
                    ) : null}
                    <StyledWrapper {...styles.section}>
                        <Link href={orderUploadLink?.url} tabIndex={0} {...styles.orderUpload}>
                            {orderUploadLink?.title}
                        </Link>
                    </StyledWrapper>
                </>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const currentAccountNavigation: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CurrentAccount),
    definition: {
        group: "Header",
        icon: "link-simple",
        fieldDefinitions: [],
        displayName: "Current Account Bar",
    },
};

export default currentAccountNavigation;
